import cx from "classnames";
import { MouseEvent, ReactNode } from "react";
import { useDispatch } from "react-redux";
import { useChannelStateContext } from "stream-chat-react";
import { setScrollToMessageId } from "store/slices/messageToScrollTo";
import { isInkleMobileApp } from "util/isInkleMobileApp";
import { parseChatMessageLink } from "util/parseChatMessageLink";

type MessageLinkProps = {
  href?: string;
  children?: ReactNode;
};

// Same-channel chat links (chaser messages, mentions, etc.) jump to the message in
// place instead of opening a new tab. Links to a different channel keep Stream's
// default new-tab behaviour.
export const MessageLink = ({ href, children }: MessageLinkProps) => {
  const dispatch = useDispatch();
  const { channel } = useChannelStateContext();

  const isEmail = href?.startsWith("mailto:");
  const isUrl = href?.startsWith("http");

  if (!href || (!isEmail && !isUrl)) {
    return <>{children}</>;
  }

  const chatLink = parseChatMessageLink(href);
  const isSameChannelMessageLink = Boolean(
    chatLink?.messageId && chatLink.channelId === channel?.id
  );

  const handleJumpToMessage = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    dispatch(setScrollToMessageId(chatLink?.messageId));
  };

  if (isSameChannelMessageLink) {
    return (
      <a
        className="str-chat__message-url-link"
        href={href}
        onClick={handleJumpToMessage}
      >
        {children}
      </a>
    );
  }

  // target="_blank" gets intercepted by the OS inside the mobile app's
  // WebView and opens the system browser instead of a new tab (WebViews
  // don't have tabs) — same-window navigation keeps it inside the app.
  const target = isInkleMobileApp() ? "_self" : "_blank";

  return (
    <a
      className={cx({ "str-chat__message-url-link": isUrl })}
      href={href}
      rel={target === "_blank" ? "nofollow noreferrer noopener" : undefined}
      target={target}
    >
      {children}
    </a>
  );
};
